/**
 * HostedMultiPeerHost — host-side 1-to-many WebRTC for >2-player hosted netplay
 * (e.g. N64 4-player). The host runs the emulator and maintains one
 * RTCPeerConnection per guest: the captured canvas video + game audio go OUT to
 * each guest, and each guest's controller input comes back IN over a per-peer
 * DataChannel.
 *
 * Modeled on SoloStreamHost (per-peer PC + per-peer signaling via the server's
 * targeted routing), plus an input DataChannel per peer. Per-peer signaling uses
 * network.sendSignalToPlayer(playerIndex, ...); inbound answers/ICE arrive tagged
 * with fromPlayer (see network.onSignal) so they route to the right peer.
 *
 * 2-player rooms keep using the single-peer HostedNetplay path; this class is
 * only instantiated when room.maxPlayers > 2.
 *
 * Usage (host):
 *   const host = new HostedMultiPeerHost(network, { fps, maxBitrate });
 *   host.setSource(canvas, audioStreamDest);     // after the core is running
 *   host.onRemoteInput = (playerIndex, bitmask, targetFrame, analogX, analogY) => { ... };
 *   host.addPeer(playerIndex);                    // on peer_joined
 *   host.handleSignal(fromPlayer, payload);       // on network.onSignal
 *   host.removePeer(playerIndex);                 // on peer_left
 *   host.destroy();
 */

import { decodeInputFrame } from './netplay/input-parity.js';
import { onAudioContextCreated } from './audio-unlock.js';

const RTC_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

export class HostedMultiPeerHost {
  /**
   * @param {import('./network.js').Network} network
   * @param {{ fps?: number, maxBitrate?: number }} opts
   */
  constructor(network, opts = {}) {
    this.network = network;
    this.fps = opts.fps || 60;
    // N64 4-player splits the screen into quadrants; give it more headroom than
    // the 2.5Mbps default so each quadrant survives WebRTC compression.
    this.maxBitrate = opts.maxBitrate || 4_000_000;

    /** @type {Map<number, { pc: RTCPeerConnection, dc: RTCDataChannel|null }>} playerIndex → peer */
    this._peers = new Map();
    this._sourceStream = null;

    // Audio capture (taps the core's AudioContext; mirrors HostedNetplay so the
    // 2-player path stays untouched). Set up via prepareAudioCapture() BEFORE the
    // core starts; the captured destination feeds setSource().
    this.audioStreamDest = null;
    this._unhookAudioCreate = null;
    this._shadowedContexts = new Map();
    this._muteGain = null;

    // Callbacks
    this.onRemoteInput = null;      // (playerIndex, bitmask, targetFrame, analogX, analogY) => {}
    this.onPeerStateChange = null;  // (playerIndex, connectionState) => {}
  }

  /** Tap the core's AudioContext.destination so game audio can be streamed to guests. */
  prepareAudioCapture() {
    this._unhookAudioCreate = onAudioContextCreated((ctx) => {
      const realDest = ctx.destination;
      const tap = ctx.createGain();
      tap.gain.value = 1;
      this._muteGain = ctx.createGain();
      tap.connect(this._muteGain);
      this._muteGain.connect(realDest);
      this.audioStreamDest = ctx.createMediaStreamDestination();
      tap.connect(this.audioStreamDest);
      const originalDesc = Object.getOwnPropertyDescriptor(ctx, 'destination');
      Object.defineProperty(ctx, 'destination', { get: () => tap, configurable: true });
      this._shadowedContexts.set(ctx, originalDesc);
    });
  }

  setLocalMuted(muted) {
    if (this._muteGain) this._muteGain.gain.value = muted ? 0 : 1;
  }

  _cleanupAudioCapture() {
    if (this._unhookAudioCreate) { this._unhookAudioCreate(); this._unhookAudioCreate = null; }
    for (const [ctx, originalDesc] of this._shadowedContexts) {
      if (originalDesc) Object.defineProperty(ctx, 'destination', originalDesc);
      else delete ctx.destination;
    }
    this._shadowedContexts.clear();
  }

  /**
   * Capture the host canvas + game audio into a single source stream, reused
   * across every peer connection (WebRTC encodes per-peer). Call once the core
   * is running. audioStreamDest comes from the host's audio-capture tap.
   */
  setSource(canvas, audioStreamDest) {
    const videoStream = canvas.captureStream();
    this._sourceStream = new MediaStream();
    for (const track of videoStream.getVideoTracks()) {
      this._sourceStream.addTrack(track);
    }
    if (audioStreamDest) {
      const audioTrack = audioStreamDest.stream.getAudioTracks()[0];
      if (audioTrack) this._sourceStream.addTrack(audioTrack);
    }
  }

  /** Open a connection to a guest at the given 0-indexed player slot. */
  async addPeer(playerIndex) {
    if (this._peers.has(playerIndex)) {
      console.warn(`[hosted-mp] peer ${playerIndex} already connected`);
      return;
    }
    if (!this._sourceStream) {
      console.warn('[hosted-mp] addPeer before setSource: no media to send');
      return;
    }

    const pc = new RTCPeerConnection(RTC_CONFIG);
    const entry = { pc, dc: null };
    this._peers.set(playerIndex, entry);

    // Same source tracks to every peer; WebRTC handles per-peer encoding.
    for (const track of this._sourceStream.getTracks()) {
      pc.addTrack(track, this._sourceStream);
    }

    for (const sender of pc.getSenders()) {
      if (sender.track?.kind === 'video') {
        try {
          const params = sender.getParameters();
          if (!params.encodings?.length) params.encodings = [{}];
          params.encodings[0].maxBitrate = this.maxBitrate;
          params.encodings[0].maxFramerate = Math.ceil(this.fps);
          await sender.setParameters(params);
        } catch { /* encoding params not supported */ }
      }
    }

    // Per-peer input channel (unreliable/low-latency, like the 2-player path).
    const dc = pc.createDataChannel('input', { ordered: false, maxRetransmits: 0 });
    dc.binaryType = 'arraybuffer';
    dc.onmessage = (e) => this._onDCMessage(playerIndex, e);
    entry.dc = dc;

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        this.network.sendSignalToPlayer(playerIndex, { type: 'ice', candidate: e.candidate });
      }
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      if (this.onPeerStateChange) this.onPeerStateChange(playerIndex, state);
      if (state === 'failed' || state === 'disconnected') {
        this.removePeer(playerIndex);
      }
    };

    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.network.sendSignalToPlayer(playerIndex, { type: 'offer', sdp: offer.sdp });
      // Install this only after the explicit initial offer. addTrack() and
      // createDataChannel() both raise negotiationneeded; wiring it sooner can
      // race a duplicate offer against the one above.
      pc.onnegotiationneeded = async () => {
        try {
          const reoffer = await pc.createOffer();
          await pc.setLocalDescription(reoffer);
          this.network.sendSignalToPlayer(playerIndex, { type: 'offer', sdp: reoffer.sdp });
        } catch (renegotiateErr) {
          console.warn(`[hosted-mp] renegotiation failed for peer ${playerIndex}:`, renegotiateErr);
        }
      };
      console.log(`[hosted-mp] sent offer to peer ${playerIndex}`);
    } catch (err) {
      console.error(`[hosted-mp] failed to offer peer ${playerIndex}:`, err);
      this.removePeer(playerIndex);
    }
  }

  /** Handle an answer/ICE (or renegotiation offer) from a specific guest. */
  async handleSignal(fromPlayer, payload) {
    const entry = this._peers.get(fromPlayer);
    if (!entry || !payload) return;
    const pc = entry.pc;
    try {
      if (payload.type === 'answer') {
        await pc.setRemoteDescription({ type: 'answer', sdp: payload.sdp });
      } else if (payload.type === 'ice' && payload.candidate) {
        await pc.addIceCandidate(payload.candidate);
      } else if (payload.type === 'offer') {
        await pc.setRemoteDescription({ type: 'offer', sdp: payload.sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.network.sendSignalToPlayer(fromPlayer, { type: 'answer', sdp: answer.sdp });
      }
    } catch (err) {
      console.warn(`[hosted-mp] signal error for peer ${fromPlayer}:`, err);
    }
  }

  _onDCMessage(playerIndex, e) {
    const data = e.data;
    if (!(data instanceof ArrayBuffer)) return;
    if (!this.onRemoteInput) return;
    const decoded = decodeInputFrame(data);
    if (decoded) {
      this.onRemoteInput(
        playerIndex,
        decoded.bitmask,
        decoded.targetFrame,
        decoded.analogX,
        decoded.analogY,
      );
    }
  }

  /** Tear down one guest's connection. */
  removePeer(playerIndex) {
    const entry = this._peers.get(playerIndex);
    if (!entry) return;
    try { entry.dc?.close(); } catch { /* ignore */ }
    try { entry.pc.close(); } catch { /* ignore */ }
    this._peers.delete(playerIndex);
    console.log(`[hosted-mp] removed peer ${playerIndex} (${this._peers.size} remaining)`);
  }

  /** Number of active guest connections. */
  get peerCount() {
    return this._peers.size;
  }

  getPeerState(playerIndex) {
    return this._peers.get(playerIndex)?.pc.connectionState || null;
  }

  destroy() {
    for (const [, entry] of this._peers) {
      try { entry.dc?.close(); } catch { /* ignore */ }
      try { entry.pc.close(); } catch { /* ignore */ }
    }
    this._peers.clear();
    this._cleanupAudioCapture();
    this.audioStreamDest = null;
    this._sourceStream = null;
  }
}
